// src/lib/kinema/core/commands/CommandRegistry.ts
import type {
  Command,
  CommandHandler,
  CommandListener,
  WildcardListener,
  EditorState,
  InverseOperation,
  Unsubscribe,
} from '../types';

export class CommandRegistry {
  private handlers = new Map<string, CommandHandler>();
  private listeners = new Map<string, Set<CommandListener>>();
  private wildcardListeners = new Set<WildcardListener>();

  registerHandler<T>(command: Command<T>, handler: CommandHandler<T>): void {
    this.handlers.set(command.type, handler as CommandHandler);
  }

  registerListener<T>(
    command: Command<T>,
    listener: CommandListener<T>,
  ): Unsubscribe {
    const key = command.type;
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener as CommandListener);
    return () => {
      this.listeners.get(key)?.delete(listener as CommandListener);
    };
  }

  registerWildcardListener(listener: WildcardListener): Unsubscribe {
    this.wildcardListeners.add(listener);
    return () => {
      this.wildcardListeners.delete(listener);
    };
  }

  execute<T>(
    command: Command<T>,
    payload: T,
    state: EditorState,
  ): { state: EditorState; inverse: InverseOperation | null } {
    const handler = this.handlers.get(command.type);
    if (!handler) {
      throw new Error(`No handler registered for command: ${command.type}`);
    }

    const result = handler(state, payload);

    // Notify typed listeners
    this.listeners.get(command.type)?.forEach((listener) => listener(payload));

    // Notify wildcard listeners
    this.wildcardListeners.forEach((listener) => listener(command, payload));

    return result;
  }

  hasHandler(command: Command): boolean {
    return this.handlers.has(command.type);
  }
}
